import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { finalNodeId, isTournamentComplete } from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { BracketNode } from './BracketNode';
import { buildBracketColumns } from './bracketLayout';

export function BracketView() {
  const { tournament, finish } = useTournament();
  const { t } = useTranslation();
  const nodeOrder = tournament?.nodeOrder;

  const complete = tournament ? isTournamentComplete(tournament) : false;
  const champion = useMemo(() => {
    if (!tournament) return null;
    const finalId = finalNodeId(tournament);
    const winnerId = finalId ? tournament.nodes[finalId]?.winner : null;
    return winnerId ? tournament.players[winnerId]?.name ?? winnerId : null;
  }, [tournament]);

  const columns = useMemo(
    () => (nodeOrder ? buildBracketColumns(nodeOrder) : []),
    [nodeOrder],
  );

  if (!nodeOrder || nodeOrder.length === 0) return null;

  const status = tournament?.status;

  // The champion name stays bold inside the translated sentence, so split the
  // template on its placeholder and interpolate manually.
  const [championBefore, championAfter = ''] = t(
    'admin.bracket.finishedChampion',
  ).split('{champion}');

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.bracket.title')}
        </Typography>
        {status === 'IN_PROGRESS' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<EmojiEventsIcon />}
            disabled={!complete}
            onClick={() => void finish()}
          >
            {t('admin.bracket.finishTournament')}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.bracket.intro')}
      </Typography>

      {status === 'FINISHED' && champion && (
        <Alert severity="success" icon={<EmojiEventsIcon />} sx={{ mb: 3 }}>
          {championBefore}
          <strong>{champion}</strong>
          {championAfter}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 5,
          overflowX: 'auto',
          pb: 2,
          alignItems: 'stretch',
        }}
      >
        {columns.map((column) => (
          <Box
            key={column.round}
            sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}
          >
            <Typography
              variant="subtitle2"
              align="center"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {t(column.titleKey, column.titleParams)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                gap: 2,
                flexGrow: 1,
              }}
            >
              {column.ids.map((id) => (
                <BracketNode key={id} nodeId={id} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
